import { ReactNode } from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { Button } from '@nf/ui';
import { CloseLineIcon, ArrowLeftIcon } from '@/components/icons/svg-icons';
import { ReactPortal } from '@/components/widgets/react-portal';
import { modalStyle } from './modal.style';
import clsx from 'clsx';
import { AutoPopupKey, useRegisterAutoPopup } from '@nf/auto-popup-handler';

const modalVariants = cva('modal', {
	variants: {
		variant: {
			center: 'modal--center',
			top: 'modal--top',
			full: modalStyle.full,
			event: 'modal--event',
			virtualWidget: 'modal--center modal--virtua-euro',
			transparent: 'modal--transparent',
		},
	},
	defaultVariants: {
		variant: 'top'
	}
});

interface ModalProps
	extends VariantProps<typeof modalVariants> {
	isOpen: boolean;
	closeModal: () => void;
	children: ReactNode;
	className?: string;
	showHeader?: boolean;
	title?: string | ReactNode;
	isBackdropClose?: boolean;
	isShowClose?: boolean;
	hasScrollbar?: boolean;
};

interface AutoPopupModalProps extends ModalProps {
	popupKey: AutoPopupKey;
};

export const Modal = ({
	variant,
	isOpen,
	closeModal,
	children,
	className,
	showHeader = true,
	title = '',
	isBackdropClose = true,
	isShowClose = true,
	hasScrollbar = false,
}: ModalProps) => {
	// 有些 Modal 放的地方外層可點擊，需要阻止事件冒泡，ex: Betslip Header 裡清除票的 Confirm Modal
	const stopPropagation = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
	}
	return isOpen ? (
		<ReactPortal wrapperId="modal-portal">
			<section className={clsx(modalVariants({ variant, className }), modalStyle.marsModal)} onClick={stopPropagation}>
				<div className="modal__overlay" onClick={isBackdropClose ? closeModal : undefined}></div>
				<div className="modal__container">
					{showHeader ? (
						<div className="modal__header">
							{ variant === 'full' ? (
								<Button variant="icon" onClick={closeModal} className={modalStyle.backButton}>
									<ArrowLeftIcon />
								</Button>
							) : null}
							{title !== '' ? (
								<div className="modal__title">
									{title}
								</div>
							): null}
							{ isShowClose ? (
								<div className="modal__close-btn">
									<Button variant="icon" onClick={closeModal}>
										<CloseLineIcon />
									</Button>
								</div>
							) : null}
						</div>
					) : null}
					<div className={clsx('modal__body', { 'scrollbar': hasScrollbar })}>
						{children}
					</div>
				</div>
			</section>
		</ReactPortal>
	): null;
};

export const AutoPopupModal = ({
	popupKey,
	children,
	isOpen,
	...props
}:AutoPopupModalProps) => {
	const canShow = useRegisterAutoPopup({
		key: popupKey,
		isEnabled: isOpen,
	});
	return (
		<Modal {...props} isOpen={canShow}>
			{children}
		</Modal>
	)
};